import React, { useState, useEffect, useRef } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  FiberManualRecord as UnreadIcon,
  CheckCircle as ReadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { Notification, useNotificationsStore } from '../../store/notificationsStore';
import { renderTextWithLinks } from '../../utils/renderWithLinksUtils';

interface NotificationAccordionProps {
  notifications: Notification[];
  expandedNotificationId?: string | null;
}

const NotificationAccordion: React.FC<NotificationAccordionProps> = ({ 
  notifications, 
  expandedNotificationId 
}) => {
  const navigate = useNavigate();
  const { markAsRead } = useNotificationsStore();
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
  const accordionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    if (expandedNotificationId) {
      setExpandedPanels(prev => 
        prev.includes(expandedNotificationId) 
          ? prev 
          : [...prev, expandedNotificationId]
      );
      
        const notification = notifications.find(n => n.id === expandedNotificationId);
        if (notification && !notification.isRead) {
          markAsRead(expandedNotificationId);
        }
    }
  }, [expandedNotificationId, markAsRead, notifications]);

    const handleAccordionChange = (notificationId: string, isExpanded: boolean) => {
    const scrollPosition = window.scrollY || window.pageYOffset;
    
    if (isExpanded) {
      setExpandedPanels(prev => [...prev, notificationId]);
      
      const notification = notifications.find(n => n.id === notificationId);
      if (notification && !notification.isRead) {
        markAsRead(notificationId);
      }
      
      requestAnimationFrame(() => {
        window.scrollTo({
          top: scrollPosition,
          behavior: 'auto'
        });
      });
    } else {
      setExpandedPanels(prev => prev.filter(id => id !== notificationId));
      
      requestAnimationFrame(() => {
        window.scrollTo({
          top: scrollPosition,
          behavior: 'auto'
        });
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPreviewText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // El backend ya ordena por fecha descendente, así que mantenemos el orden original
  // No ordenamos aquí para preservar el orden que viene del backend

  return (
    <Box>
      {notifications.map((notification) => {
        const isExpanded = expandedPanels.includes(notification.id);
        
        return (
          <Accordion
            key={notification.id}
            ref={(el) => {
              accordionRefs.current[notification.id] = el;
            }}
            expanded={isExpanded}
            onChange={(_, isExpanded) => handleAccordionChange(notification.id, isExpanded)}
            sx={{
              mb: 1,
              backgroundColor: notification.isRead ? 'background.paper' : '#f5f5f5',
              '&:before': {
                display: 'none',
              },
              boxShadow: notification.isRead ? 1 : 2,
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  alignItems: 'flex-start',
                  gap: 1,
                  flexDirection: 'column',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Box
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    p: 0.5,
                    flexShrink: 0
                  }}
                >
                  {notification.isRead ? (
                    <ReadIcon color="success" fontSize="small" />
                  ) : (
                    <UnreadIcon color="primary" fontSize="small" />
                  )}
                </Box>

                <Box sx={{ flexGrow: 1, minWidth: 0, pl: 1 }}>
                  <Typography 
                    sx={{
                      fontWeight: 400,
                      fontSize: { xs: '10px', sm: '11px', md: '12px' },
                      lineHeight: '1.2',
                      letterSpacing: '0%',
                      color: 'text.secondary',
                      mb: 0.5
                    }}
                  >
                    {formatDate(notification.date)}
                  </Typography>

                  <Typography
                    sx={{
                      fontWeight: notification.isRead ? 400 : 700,
                      fontSize: { xs: '14px', sm: '16px', md: '18px' },
                      lineHeight: { xs: '1.2', sm: '1.1' },
                      letterSpacing: '0%',
                      color: '#585858',
                      wordBreak: 'break-word',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: { xs: 2, sm: 1 },
                      WebkitBoxOrient: 'vertical',
                    }}
                  >
                    {notification.title}
                  </Typography>

                  {!isExpanded && (
                    <Typography
                      sx={{
                        fontWeight: 400,
                        fontSize: { xs: '11px', sm: '12px', md: '13px' },
                        lineHeight: '1.3',
                        color: '#9e9e9e',
                        mt: 0.5,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: { xs: 2, sm: 1 },
                        WebkitBoxOrient: 'vertical',
                        wordBreak: 'break-word',
                      }}
                    >
                      {renderTextWithLinks(getPreviewText(notification.description, 120), { navigate })}
                    </Typography>
                  )}
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ pl: 7.5 }}>
              <Typography 
                sx={{
                  fontWeight: 400,
                  fontSize: { xs: '12px', sm: '14px', md: '16px' },
                  letterSpacing: '2%',
                  color: '#585858',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {renderTextWithLinks(notification.description, { navigate })}
              </Typography>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};

export default NotificationAccordion;